import {useState, useEffect, useMemo} from 'react';
import {initIndex} from 'Callbacks/algolia/searchClient';
import Pager from 'Components/Pager';
import CardList from 'Components/ProgramFinder/CardList/saf';
import SidebarFilters from 'Components/ProgramFinder/SidebarFilters/saf';
import ProgramFinderMeta from 'Components/ProgramFinder/ProgramFinderMeta';
import scrollTo from 'utils/scrollTo';
import parseQuery from 'utils/parseQuery';
import getFormConfig from 'config/saf/FormConfigs/getFormConfig';
import {collectFilterQuery, collectFacetQuery} from 'utils/algoliaFiltration';
import SearchHeader from 'Components/SearchHeader/saf';

const Algolia = ({currentLanguage, disciplines, universities, pgmtypes}) => {
  const {search: urlParams, pathname} = window.location;
  const params = parseQuery(urlParams);
  const {
    programFinderTextField,
    programFinderSidebarFilters,
    programFinderSorting
  } = getFormConfig();

  const typeOfStudyFromUrl = params.type_of_study ? params.type_of_study.split(' & ').map((item) => `field_prgm_type:"${item}"`) : [];
  const universitiesFromUrl = params.universities ? params.universities.split(' & ').map((item) => {
    return {
      value: `field_prgm_university_nid:${item.split(' ~ ')[0]}`,
      label: `${item.split(' ~ ')[1]}`,
    }
  }) : [];

  const initialFacets = {
    field_prgm_delivery_type: [],
    field_prgm_housing_opt: [],
    field_prgm_academic_level: [],
    field_prgm_rank_group: [],
    field_prgm_lang_instruct: [],
    field_prgm_lang_pre_lvl: [],
    field_prgm_term_type: [],
    regions: params && params.region ? params.region : '',
    destinations: []
  };
  const initialFilters = {
    field_prgm_type: typeOfStudyFromUrl,
    price_range: '',
    gpa_range: '',
    language: `search_api_language:${currentLanguage}`,
    field_prgm_credits_available: '',
    field_prgm_disciplines_tid: [],
    field_prgm_university_nid: universitiesFromUrl,
  }
  const facets = [
    'destinations',
    'field_prgm_academic_level',
    'field_prgm_cost_est',
    'field_prgm_credits_available',
    'field_prgm_delivery_type',
    'field_prgm_housing_opt',
    'field_prgm_lang_instruct',
    'field_prgm_lang_pre_lvl',
    'field_prgm_rank_group',
    'field_prgm_term_type',
    'regions'
  ];
  const [indexState, setIndex] = useState(initIndex(window.drupalSettings.algoliaSearch.programFinder.default))
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState({
    query: '',
  });
  const [facetFilters, setFacetFilters] = useState(initialFacets);
  const [result, setResult] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [filtersVisibility, setFiltersVisibility] = useState(false);
  const [sortState, setSorting] = useState({
    sortBy: window.drupalSettings.algoliaSearch.programFinder.default
  });

  const search = (updatePager) => {
    if (updatePager && page !== 0) {
      // If page is not set to 0 exit search callback. On page state update search will be called again.
      setPage(0);
      return;
    }

    indexState.search(query.query, {
      page,
      hitsPerPage: process.env.REACT_APP_ALGOLIA_DEFAULT_HITS_PER_PAGE_SAF,
      facets: facets,
      facetFilters: prepareFacetQuery,
      filters: prepareFiltersQuery,
    }).then(res => {
      setResult(res);
    }).catch((error) => {
      console.error(error);
    });
  };

  const prepareFacetQuery = useMemo(() => collectFacetQuery(facetFilters), [facetFilters]);
  const prepareFiltersQuery = useMemo(() => collectFilterQuery(filters), [filters]);

  useEffect(() => {
    window.history.replaceState({}, document.title, pathname);
  }, []);

  useEffect(() => {
    setIndex(initIndex(sortState.sortBy));
  }, [sortState]);

  useEffect(search, [page, facetFilters, prepareFacetQuery, currentLanguage, filters, indexState]);

  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    }
  }, [facetFilters, filters, sortState]);

  if (!result) {
    return '';
  }

  const clearFacets = (event) => {
    event.preventDefault();
    setFacetFilters({
      ...initialFacets,
      regions: ""
    });
    setFilters({
      ...initialFilters,
    });
  };

  const changePage = (event, newPage) => {
    event.preventDefault();
    if (newPage === page) {
      return '';
    }

    setPage(newPage);
    setTimeout(() => {
      scrollTo();
    }, 100);
  };

  return (
    <div className="program-finder">
      <SearchHeader formConfig={programFinderTextField[0]}
                    formState={query}
                    setFormState={setQuery}
                    searchCallback={search}
                    setFiltersState={setFiltersVisibility}
                    searchButton="Algolia.SearchPage.SearchButton.Title"
                    title="Algolia.SearchPage.Heading"/>

      <div className="lg:flex">
        <SidebarFilters facets={result.facets}
                        formConfig={programFinderSidebarFilters}
                        formState={facetFilters}
                        setFormState={setFacetFilters}
                        clearCallback={clearFacets}
                        filtersVisibility={filtersVisibility}
                        setFiltersVisibility={setFiltersVisibility}
                        filtersState={filters}
                        setFilters={setFilters}
                        sortingFilters={programFinderSorting}
                        sortState={sortState}
                        setSortState={setSorting}
                        disciplines={disciplines}
                        universities={universities}
                        pgmType={pgmtypes}/>
        <div className="flex-grow">
          <ProgramFinderMeta hitsNr={result.nbHits}
                             sortingForm={programFinderSorting}
                             sortState={sortState}
                             setSortState={setSorting}
                             resultsText="Algolia.SearchPage.ResultsMeta"/>
          <CardList list={result ? result.hits : []}/>
        </div>
      </div>

      {result &&
        <Pager
          meta={{
            totalRows: result.nbHits,
            pageSize: result.hitsPerPage,
            totalPages: result.nbPages,
            pageNumber: result.page
          }}
          activePage={result.page}
          changePage={changePage}
        />
      }

    </div>
  );
}

export default Algolia;
